import { resolveTaskId } from './resolveTaskOccurrence';
import request from '../../app/api/requests';
import { getSessionEpoch } from '../lib/auth/session';
import { activateTimer } from '../lib/store/RunTimeTaskResults/RunTimeResults';
import { timerTaskId, elapsedSeconds } from '../lib/taskTimer';
import { timerPush } from './timerPush';
import { addRunTime } from './addRunTime';
import { taskCreated } from '../lib/store/taskUpdatesReducer';

// Serialize commands within one store. Server-side concurrency across devices is separate.
const queues = new WeakMap();
const syncs = new WeakMap();
const timerState = getState => getState().stopWatchesData;
const assertSession = epoch => {
    if (getSessionEpoch() !== epoch) throw new Error('Session changed');
};
function enqueue(getState, epoch, work) {
    const previous = queues.get(getState);
    const result = Promise.resolve(previous).catch(() => {}).then(() => {
        assertSession(epoch);
        return work();
    });
    queues.set(getState, result);
    const cleanup = () => { if (queues.get(getState) === result) queues.delete(getState); };
    result.then(cleanup, cleanup);
    return result;
}
async function getRunTime(id) {
    const databaseId = await resolveTaskId(id);
    const { data } = await request.get('/Time/RunTime', { params: { TaskId: databaseId } });
    if (typeof data !== 'number' || !Number.isFinite(data) || data < 0) throw new Error('Invalid run time');
    return data;
}
async function getActive() {
    const { data } = await request.get('/Timer', { params: { UserId: localStorage.getItem('userID') } });
    if (!data || (data.taskId != null && (!timerTaskId(data.taskId) || !Number.isFinite(data.startTime)))) {
        throw new Error('Invalid timer response');
    }
    return data.taskId == null ? null : { taskId: timerTaskId(data.occurrenceId ?? data.taskId), startTime: data.startTime };
}
async function refresh(dispatch, getState, epoch) {
    const revision = timerState(getState).revision;
    const active = await getActive();
    assertSession(epoch);
    const ids = new Set(timerState(getState).stopWatchesData.map(t => t.taskId));
    if (active) ids.add(active.taskId);
    const entries = await Promise.all([...ids].map(async taskId => ({ taskId,
        isTimerActive: active?.taskId === taskId,
        startTime: active?.taskId === taskId ? active.startTime : null,
        runTime: await getRunTime(taskId),
    })));
    assertSession(epoch);
    dispatch({ type: 'timer/snapshot', revision, payload: entries });
    return active ? entries.find(t => t.taskId === active.taskId) : null;
}
export const syncTaskTimer = ({force = false} = {}) => (dispatch, getState) => {
    const epoch = getSessionEpoch();
    if (force) dispatch({type: 'timer/stale'});
    const current = syncs.get(getState);
    if (current?.epoch === epoch) {
        if (force) current.again = true;
        return current.promise;
    }
    const entry = {epoch, again: false, promise: null};
    const promise = enqueue(getState, epoch, async () => {
        let active;
        // An event arriving during a GET needs a fresh read after that response.
        do {
            entry.again = false;
            active = await refresh(dispatch, getState, epoch);
        } while (entry.again);
        if (!timerState(getState).pendingSave) dispatch({ type: 'timer/error', payload: '' });
        return active;
    }).catch(error => {
        if (getSessionEpoch() === epoch) dispatch({ type: 'timer/error', payload: 'Не удалось загрузить таймер. Повторите попытку.' });
        throw error;
    });
    entry.promise = promise;
    syncs.set(getState, entry);
    const cleanup = () => { if (syncs.get(getState) === entry) syncs.delete(getState); };
    promise.then(cleanup, cleanup);
    return promise;
};

// A lost response must be reconciled before repeating a write. Keep the intent until confirmed.
async function finishPending(dispatch, getState, epoch) {
    const pending = timerState(getState).pendingSave;
    if (!pending) return;
    const active = await getActive();
    assertSession(epoch);
    if (active?.taskId === pending.taskId) {
        if (active.startTime !== pending.startTime) throw new Error('Timer changed on another device');
        await dispatch(timerPush({ taskId: pending.taskId, timerIsActive: false, startTime: pending.endTime }));
        assertSession(epoch);
    }
    const current = await getRunTime(pending.taskId);
    assertSession(epoch);
    if (current !== pending.seconds) {
        if (current !== pending.before) throw new Error('Run time changed on another device');
        await dispatch(addRunTime({ id: pending.taskId, seconds: pending.seconds }));
        assertSession(epoch);
    }
    dispatch(activateTimer({ taskId: pending.taskId, isTimerActive: false, startTime: null, runTime: pending.seconds }));
    dispatch({ type: 'timer/pending', payload: null });
    return true;
}
function confirmedActive(getState) {
    return timerState(getState).stopWatchesData.find(t => t.isTimerActive) ?? null;
}
async function activeForCommand(dispatch, getState, epoch) {
    const state = timerState(getState);
    if (!state.initialized || state.stale || Date.now() - state.syncedAt > 30000) return refresh(dispatch, getState, epoch);
    return confirmedActive(getState);
}
async function confirmedRunTime(task, getState) {
    const id = timerTaskId(task);
    const entry = timerState(getState).stopWatchesData.find(t => t.taskId === id);
    const value = entry?.runTime ?? (typeof task === 'object' ? task.runTime : undefined);
    return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : getRunTime(id);
}
function savedPending(dispatch, pending) {
    dispatch(activateTimer({taskId: pending.taskId, isTimerActive: false, startTime: null, runTime: pending.seconds}));
    dispatch({type: 'timer/pending', payload: null});
}
async function stopActive(dispatch, getState, epoch, active, requestedEnd = Date.now()) {
    if (!active) return;
    const before = active.runTime ?? await confirmedRunTime({id: active.taskId}, getState);
    assertSession(epoch);
    const endTime = Math.max(requestedEnd, active.startTime);
    const pending = {taskId: active.taskId, startTime: active.startTime, endTime, before,
        seconds: elapsedSeconds(before, active.startTime, endTime)};
    dispatch({type: 'timer/pending', payload: pending});
    // Normal path: confirmed snapshot -> stop -> absolute total. Read-back is only for recovery.
    await dispatch(timerPush({taskId: active.taskId, timerIsActive: false, startTime: endTime}));
    assertSession(epoch);
    if (pending.seconds !== before) await dispatch(addRunTime({id: active.taskId, seconds: pending.seconds}));
    assertSession(epoch);
    savedPending(dispatch, pending);
    return true;
}
function command(work) {
    return (dispatch, getState) => {
        const epoch = getSessionEpoch();
        return enqueue(getState, epoch, async () => {
            dispatch({ type: 'timer/busy', payload: true });
            dispatch({ type: 'timer/error', payload: '' });
            try {
                const recoveredSave = await finishPending(dispatch, getState, epoch);
                const result = await work(dispatch, getState, epoch);
                assertSession(epoch);
                if (recoveredSave || result) {
                    dispatch({ type: 'timer/changed' });
                    dispatch(taskCreated());
                }
                return result;
            } catch (error) {
                if (getSessionEpoch() === epoch) {
                    // Reconcile status without automatically repeating the failed save.
                    await refresh(dispatch, getState, epoch).catch(() => {});
                    if (getSessionEpoch() === epoch) dispatch({ type: 'timer/error', payload:
                        'Не удалось сохранить таймер. Проверьте соединение и повторите попытку.' });
                }
                throw error;
            } finally {
                if (getSessionEpoch() === epoch) dispatch({ type: 'timer/busy', payload: false });
            }
        });
    };
}
export const startTaskTimer = task => command(async (dispatch, getState, epoch) => {
    const id = timerTaskId(task);
    if (!id) throw new Error('Task has no timer ID');
    const active = await activeForCommand(dispatch, getState, epoch);
    if (active?.taskId === id) return;
    await stopActive(dispatch, getState, epoch, active);
    const runTime = await confirmedRunTime(task, getState);
    assertSession(epoch);
    const startTime = Date.now();
    await dispatch(timerPush({ taskId: id, startTime, timerIsActive: true }));
    assertSession(epoch);
    dispatch(activateTimer({ taskId: id, startTime, isTimerActive: true, runTime }));
    return true;
});
export const stopTaskTimer = task => {
    const requestedEnd = Date.now();
    return command(async (dispatch, getState, epoch) => {
        const id = timerTaskId(task);
        if (!id) return;
        const active = await activeForCommand(dispatch, getState, epoch);
        if (active?.taskId === id) return stopActive(dispatch, getState, epoch, active, requestedEnd);
    });
};
export const saveTaskRunTime = (task, seconds) => command(async (dispatch, getState, epoch) => {
    const id = timerTaskId(task);
    if (!id || !Number.isFinite(seconds) || seconds < 0) throw new Error('Invalid run time');
    const active = await activeForCommand(dispatch, getState, epoch);
    if (active?.taskId === id) await stopActive(dispatch, getState, epoch, active);
    const before = await confirmedRunTime(task, getState);
    assertSession(epoch);
    const pending = {taskId: id, startTime: null, endTime: Date.now(), before, seconds: Math.floor(seconds)};
    dispatch({type: 'timer/pending', payload: pending});
    if (pending.seconds !== before) await dispatch(addRunTime({id, seconds: pending.seconds}));
    assertSession(epoch);
    savedPending(dispatch, pending);
    return true;
});
export const retryTimerSave = () => command(async () => {});
